import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import Button from 'partial/components/Button';
import FormInput from 'partial/components/form/FormInput';
import FormInputNumber from 'partial/components/form/FormInputNumber';
import { RadioGroup } from '@headlessui/react';
import Form from 'partial/components/form';
import { usePlansContext } from 'partial/wrappers/PlansContext';
import {
  ArrowLeft,
  Circle as CircleIcon,
  RadioButton as CheckIcon,
} from '@phosphor-icons/react';
import { useShowPlan } from '../hooks';

const options = [
  {
    label: 'Yes',
    value: 1,
  },
  {
    label: 'No',
    value: 0,
  },
];

const Radio = ({ val, onChange }) => (
  <RadioGroup value={val} onChange={onChange}>
    <div className="flex gap-4">
      {options.map(({ label, value }) => (
        <RadioGroup.Option key={label} value={value}>
          {({ checked }) => (
            <div className="flex w-full items-center gap-2">
              <div className="shrink-0 text-brand-600 cursor-pointer">
                {checked ? (
                  <CheckIcon className="h-4 w-4" />
                ) : (
                  <CircleIcon className="h-4 w-4" />
                )}
              </div>
              <div className="flex items-center">
                <div className="text-sm">
                  <RadioGroup.Label
                    as="p"
                    className="font-[500] flex-grow text-sm tracking-wide text-neutral-900"
                  >
                    {label}
                  </RadioGroup.Label>
                </div>
              </div>
            </div>
          )}
        </RadioGroup.Option>
      ))}
    </div>
  </RadioGroup>
);

Radio.propTypes = {
  val: PropTypes.instanceOf(Number).isRequired,
  onChange: PropTypes.func.isRequired,
};

function UpdatePlan() {
  const navigate = useNavigate();
  const { values, setFieldValue, errors } = usePlansContext();

  const params = useParams();
  const { planId } = params;
  const [isLoading, plan] = useShowPlan(planId);

  useEffect(() => {
    if (!plan) return;
    setFieldValue('uuid', planId);
    setFieldValue('name', plan?.name);
    setFieldValue('rate_per_sms', plan?.rate_per_sms);
    setFieldValue('mask_name_limit', plan?.mask_name_limit);
    setFieldValue('max_purchase_limit', plan?.max_purchase_limit);
    setFieldValue('credit_limit', plan?.credit_limit);
    setFieldValue('description', plan?.description);
    setFieldValue('is_need_approval', plan?.is_need_approval);
    setFieldValue('is_default', plan?.is_default);
  }, [plan, setFieldValue, planId]);

  if (isLoading)
    return (
      <div className="grid grid-cols-2 gap-3 p-3 w-full h-fit">
        <div className="col-span-2 animate-pulse bg-neutral-300 h-8 w-full rounded" />
        <div className="col-span-2 animate-pulse bg-neutral-300 h-8 w-full rounded" />
        <div className="col-span-2 animate-pulse bg-neutral-300 h-8 w-full rounded" />
        <div className="col-span-2 animate-pulse bg-neutral-300 h-8 w-full rounded" />
        <div className="col-span-2 animate-pulse bg-neutral-300 h-8 w-full rounded" />
      </div>
    );

  return (
    <div className="flex-grow pl-4">
      <header className="flex items-center justify-between pl-8 pr-4 py-[6px] rounded-sm bg-brand-600 ">
        {plan && (
          <h2 className="text-lg font-semibold text-white tracking-wider">
            {plan?.name} PLAN
          </h2>
        )}
        <div>
          <Button onClick={() => navigate(-1)} icon={ArrowLeft} size="xxs" link>
            <span>Back</span>
          </Button>
        </div>
      </header>
      <Form error={errors} noValidate>
        <div className="space-y-6 px-8 py-10">
          <div className="flex items-center">
            <h6 className="w-[200px] text-sm tracking-wide">Name</h6>
            <div className="flex-1">
              <FormInput
                name="name"
                onSetFieldValue={setFieldValue}
                value={values.name}
                error={errors.name}
                upperCase
              />
            </div>
          </div>
          <div className="flex items-center">
            <h6 className="w-[200px] text-sm tracking-wide">Rate per SMS</h6>
            <div className="flex-1">
              <FormInputNumber
                name="rate_per_sms"
                onSetFieldValue={setFieldValue}
                value={values.rate_per_sms}
                error={errors.rate_per_sms}
              />
            </div>
          </div>
          <div className="flex items-center">
            <h6 className="w-[200px] text-sm tracking-wide">Sender Name</h6>
            <div className="flex-1">
              <FormInputNumber
                name="mask_name_limit"
                onSetFieldValue={setFieldValue}
                value={values.mask_name_limit}
                error={errors.mask_name_limit}
              />
              <div className="text-xs text-neutral-700 mt-1">
                A value of &apos;0&apos; means unlimited
              </div>
            </div>
          </div>
          <div className="flex items-center">
            <h6 className="w-[200px] text-sm tracking-wide">Purchase Limit</h6>
            <div className="flex-1">
              <FormInputNumber
                name="max_purchase_limit"
                onSetFieldValue={setFieldValue}
                value={values.max_purchase_limit}
                error={errors.max_purchase_limit}
              />
              <div className="text-xs text-neutral-700 mt-1">
                A value of &apos;0&apos; means unlimited
              </div>
            </div>
          </div>
          <div className="flex items-center">
            <h6 className="w-[200px] text-sm tracking-wide">Credit Limit</h6>
            <div className="flex-1">
              <FormInputNumber
                name="credit_limit"
                onSetFieldValue={setFieldValue}
                value={values.credit_limit}
                error={errors.credit_limit}
              />
              <div className="text-xs text-neutral-700 mt-1">
                A value of &apos;0&apos; means unlimited
              </div>
            </div>
          </div>
          <div className="flex items-center">
            <h6 className="w-[200px] text-sm tracking-wide">Description</h6>
            <div className="flex-1">
              <FormInput
                name="description"
                onSetFieldValue={setFieldValue}
                value={values.description}
                error={errors.description}
              />
            </div>
          </div>
          <div className="flex items-center !mt-8">
            <h6 className="w-[200px] text-sm tracking-wide">
              Upgrade Approval
            </h6>
            <Radio
              val={values.is_need_approval}
              onChange={(val) => setFieldValue('is_need_approval', val)}
            />
          </div>
          <div className="flex items-center">
            <h6 className="w-[200px] text-sm tracking-wide">Default Plan</h6>
            <Radio
              val={values.is_default}
              onChange={(val) => setFieldValue('is_default', val)}
            />
          </div>
        </div>
      </Form>
    </div>
  );
}

export default UpdatePlan;
